/**
 * @component Playbar
 *
 * @description A comprehensive video playback control component that combines a progress bar and playhead.
 * Displays the current progress of media playback and allows seeking by clicking or dragging.
 *
 * @example
 * // Basic usage
 * <Playbar
 *   ref={playbarRef}
 *   duration={videoDuration}
 *   currentTime={videoCurrentTime}
 *   onSeek={handleSeek}
 *   onDragStart={handleDragStart}
 *   onDragEnd={handleDragEnd}
 *   onDrag={handleDrag}
 * />
 */

import React, {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import { View } from '@instructure/ui';
import ProgressBar from '../ProgressBar';
import Playhead from '../Playhead';

const Playbar = forwardRef(
  (
    {
      // Required props
      duration = 0,
      currentTime = 0,
      onSeek,

      // Optional props with defaults
      onDragStart = null,
      onDragEnd = null,
      onDrag = null,
      isLive = false,
      displayMarkers = [],

      // Progress bar styling
      height = '8px',
      borderRadius = 'medium',
      backgroundColor = '#E0E0E0',
      progressColor = 'brand',
      liveColor = 'success',

      // Playhead styling
      handleColor = '#fff',
      handleBorderColor = '#333',
      handleSize = 16,
      handleLineColor = '#000',

      // Container styling
      containerStyle = {},
      ...otherProps
    },
    ref
  ) => {
    // Internal refs for the components
    const containerRef = useRef(null);
    const progressBarRef = useRef(null);

    // State for internal component management
    const [dragging, setDragging] = useState(false);
    const [scrubTime, setScrubTime] = useState(currentTime);

    // Expose the container ref to parent components
    useImperativeHandle(ref, () => containerRef.current);

    // Handle seeking from the progress bar
    const handleProgressBarSeek = (time) => {
      if (!dragging && onSeek) {
        onSeek(time);
      }
    };

    // Handle drag start event
    const handleDragStart = (time) => {
      setDragging(true);
      setScrubTime(time);
      if (onDragStart) {
        onDragStart(time);
      }
    };

    // Handle drag event
    const handleDrag = (time) => {
      setScrubTime(time);
      if (onDrag) {
        onDrag(time);
      }
    };

    // Handle drag end event
    const handleDragEnd = (time) => {
      setDragging(false);
      if (onSeek) {
        onSeek(time);
      }
      if (onDragEnd) {
        onDragEnd(time);
      }
    };

    // Handle direct clicks on the container
    const handleContainerClick = (e) => {
      // Only handle clicks if we're not already dragging
      if (!dragging && onSeek) {
        const rect = containerRef.current.getBoundingClientRect();
        let relativeX = e.clientX - rect.left;
        relativeX = Math.max(0, Math.min(relativeX, rect.width));
        const newTime = (relativeX / rect.width) * duration;
        console.log('PlayBar click:', newTime);

        onSeek(newTime);
      }
    };

    return (
      <View
        as="div"
        position="relative"
        withVisualDebug={false}
        elementRef={(el) => {
          containerRef.current = el;
        }}
        onClick={handleContainerClick}
        {...otherProps}
      >
        <div style={{ position: 'relative' }}>
          {/* Progress Bar Component */}
          <ProgressBar
            ref={progressBarRef}
            duration={duration}
            currentTime={currentTime}
            onSeek={handleProgressBarSeek}
            isLive={isLive}
            dragging={dragging}
            scrubTime={scrubTime}
            height={height}
            borderRadius={borderRadius}
            backgroundColor={backgroundColor}
            progressColor={progressColor}
            liveColor={liveColor}
            style={containerStyle}
          />

          {/* Playhead Component - Positioned absolutely on top of the progress bar */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
            <Playhead
              ref={progressBarRef}
              duration={duration}
              currentTime={currentTime}
              onSeek={handleProgressBarSeek}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDrag={handleDrag}
              isLive={isLive}
              dragging={dragging}
              scrubTime={scrubTime}
              displayMarkers={displayMarkers}
              handleColor={handleColor}
              handleBorderColor={handleBorderColor}
              handleSize={handleSize}
              handleLineColor={handleLineColor}
              handleStyle={otherProps.handleStyle}
              handleLineStyle={otherProps.handleLineStyle}
            />
          </div>
        </div>
      </View>
    );
  }
);

/**
 * PropTypes for the Playbar component
 */
Playbar.propTypes = {
  /**
   * The total duration of the media in seconds
   */
  duration: PropTypes.number,

  /**
   * The current playback position in seconds
   */
  currentTime: PropTypes.number,

  /**
   * Callback function called when the user seeks to a new position
   * @param {number} time - The new time position in seconds
   */
  onSeek: PropTypes.func.isRequired,

  /**
   * Callback function called when the user starts dragging the playhead
   * @param {number} time - The time position when dragging started
   */
  onDragStart: PropTypes.func,

  /**
   * Callback function called when the user ends dragging the playhead
   * @param {number} time - The final time position after dragging
   */
  onDragEnd: PropTypes.func,

  /**
   * Callback function called during dragging of the playhead
   * @param {number} time - The current time position during dragging
   */
  onDrag: PropTypes.func,

  /**
   * Whether the media is being played live
   */
  isLive: PropTypes.bool,

  /**
   * Array of markers to display on the timeline
   */
  displayMarkers: PropTypes.array,

  /**
   * The height of the progress bar
   */
  height: PropTypes.string,

  /**
   * The border radius of the progress bar
   * Can be 'small', 'medium', 'large', or a specific pixel value
   */
  borderRadius: PropTypes.string,

  /**
   * The background color of the progress bar track
   */
  backgroundColor: PropTypes.string,

  /**
   * The color of the progress indicator
   * Can be a theme color like 'brand', 'primary', or a hex value
   */
  progressColor: PropTypes.string,

  /**
   * The color used when in live mode
   * Can be a theme color like 'success', 'error', or a hex value
   */
  liveColor: PropTypes.string,

  /**
   * Color of the playhead handle
   */
  handleColor: PropTypes.string,

  /**
   * Border color of the playhead handle
   */
  handleBorderColor: PropTypes.string,

  /**
   * Size of the playhead handle in pixels
   */
  handleSize: PropTypes.number,

  /**
   * Color of the playhead line
   */
  handleLineColor: PropTypes.string,

  /**
   * Additional styles for the container element
   */
  containerStyle: PropTypes.object,

  /**
   * Additional styles for the handle element
   */
  handleStyle: PropTypes.object,

  /**
   * Additional styles for the handle line element
   */
  handleLineStyle: PropTypes.object,
};

export default Playbar;
